import React, { useState } from 'react';
import { Search, MapPin, Eye, Settings, Crown } from 'lucide-react';

interface Village {
  id: string;
  name: string;
  icon: string;
  heritage: string;
}

interface Props {
  setActiveTab: (tab: string) => void;
  onSelectVillage?: (village: Village) => void;
  currentUser?: any;
}

const VILLAGES: Village[] = [
  { id: 'v01',  name: 'Jattu-Uzairue',       icon: '👑', heritage: 'Royal seat, palace, administrative capital' },
  { id: 'v02',  name: 'Afashio-Uzairue',      icon: '🛡️', heritage: 'Egbele age-grade, craftsmanship' },
  { id: 'v03',  name: 'Afowa-Uzairue',        icon: '🌳', heritage: 'Founding settlement, iroko grove traditions' },
  { id: 'v04',  name: 'Apana-Uzairue',        icon: '🌊', heritage: 'River heritage, fishing rites, Apana Road Race' },
  { id: 'v05',  name: 'Ayaoghena-Uzairue',    icon: '🎭', heritage: 'Ancestral pathway, memorial masquerades' },
  { id: 'v06',  name: 'Ayogwiri-Uzairue',     icon: '⚔️', heritage: 'Warrior heritage, traditional wrestling' },
  { id: 'v07',  name: 'Ayua-Uzairue',         icon: '🌿', heritage: 'Spiritual authority, traditional medicine' },
  { id: 'v08',  name: 'Elele-Uzairue',        icon: '🌾', heritage: 'Agricultural sanctuary, New Yam & Iko Ebo' },
  { id: 'v09',  name: 'Idatto-Uzairue',       icon: '🏺', heritage: 'Pottery, women-led clay artisanship' },
  { id: 'v10', name: 'Ikabigbo-Uzairue',     icon: '🥊', heritage: 'Traditional wrestling, wood carving' },
  { id: 'v11', name: 'Ikholo-Uzairue',       icon: '⚖️', heritage: 'Mediation, elder council, peacemaking' },
  { id: 'v12', name: 'Imeke-Uzairue',        icon: '🧵', heritage: 'Master weaving, Etsako ceremonial textiles' },
  { id: 'v13', name: 'Imhonikhe-Uzairue',    icon: '🏛️', heritage: 'Ancestral shrine custodians, Odion elders' },
  { id: 'v14', name: 'Irekpai-Uzairue',      icon: '⚒️', heritage: 'Blacksmithing, iron working' },
  { id: 'v15', name: 'Iyamho-Uzairue',       icon: '📚', heritage: 'Education, intellectual heritage, AAU corridor' },
  { id: 'v16', name: 'Iyora-Uzairue',        icon: '🎪', heritage: 'Ekaba Festival custodian, sacred masquerade' },
  { id: 'v17', name: 'Iyuku-Uzairue',        icon: '🏪', heritage: 'Market and trade heritage' },
  { id: 'v18', name: 'Ogbido-Uzairue',       icon: '🌿', heritage: 'Pottery, herbal medicine, forest stewardship' },
  { id: 'v19', name: 'Ozor-Uzairue',         icon: '🌲', heritage: "Hunters' Guild, forest knowledge" },
  { id: 'v20', name: 'Ugbenor-Uzairue',      icon: '🌴', heritage: 'Palm oil, women-led cooperatives' },
  { id: 'v21', name: 'Uluoke-Uzairue',       icon: '⛰️', heritage: 'Elevated terrain, sentinel heritage' },
  { id: 'v22', name: 'Yelwa-Uzairue',        icon: '💰', heritage: 'Commercial prosperity, trade-route heritage' },
  // ── Special Communities ──────────────────────────────────────────────
  { id: 'sp01', name: 'Igala Community',    icon: '🤝', heritage: 'Igala settlers — brothers in peace, trade and kinship' },
  { id: 'sp02', name: 'Igbo Community',     icon: '🌟', heritage: 'Igbo community — long-standing neighbours and cultural partners' },
  { id: 'sp03', name: 'Yoruba Community',   icon: '🎺', heritage: 'Yoruba community — vibrant cultural exchange and commerce' },
  { id: 'sp04', name: 'Diaspora Community', icon: '✈️', heritage: 'Sons and daughters of Uzairue Kingdom across the globe' },
];

const isAdmin = (user: any) =>
  user?.systemRole === 'PLATFORM_ADMIN' ||
  user?.role === 'admin' || user?.role === 'super_admin' ||
  user?.email === 'erueuzairue@gmail.com';

export const CommunitiesShowcase: React.FC<Props> = ({
  setActiveTab, onSelectVillage, currentUser
}) => {
  const [search, setSearch] = useState('');

  const filtered = VILLAGES.filter(v =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    v.heritage.toLowerCase().includes(search.toLowerCase())
  );

  const handleViewPortal = (v: Village) => {
    if (onSelectVillage) onSelectVillage(v);
  };

  return (
    <div style={{ background:"#070D0A", minHeight:"100vh", padding:"24px 16px" }}>
      {/* Header */}
      <div style={{ textAlign:"center" as const, marginBottom:24 }}>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"4px 12px", borderRadius:20, fontSize:10, fontWeight:700, letterSpacing:2, background:"rgba(201,168,76,0.12)", border:"1px solid rgba(201,168,76,0.3)", color:"#C9A84C" }}>
          <Crown className="w-3 h-3" /> Uzairue Kingdom
        </div>
        <h2 style={{ fontFamily:"Cinzel,serif", fontSize:28, fontWeight:700, color:"#EDE8DC", margin:0 }}>
          22 Villages & Component Communities
        </h2>
        <p style={{ color:"rgba(237,232,220,0.5)", fontSize:13, maxWidth:480, margin:"8px auto 0" }}>
          Explore the 22 villages and 4 special communities of Uzairue Kingdom,
          Etsako West LGA, Edo State, Nigeria. One People, One Kingdom, One Destiny.
        </p>
      </div>

      {/* Search */}
      <div style={{ position:"relative" as const, maxWidth:480, margin:"0 auto" }}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(237,232,220,0.35)" }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search communities..."
          style={{ width:"100%", background:"#0E1812", border:"1px solid #1C2E22", borderRadius:12, padding:"10px 16px 10px 40px", color:"#EDE8DC", fontSize:13, outline:"none" }}
        />
      </div>

      {/* Count */}
      <p style={{ textAlign:"center" as const, color:"rgba(237,232,220,0.3)", fontSize:12 }}>
        Showing {filtered.length} of {VILLAGES.length} communities
      </p>

      {/* Community grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16 }}>
        {filtered.map(v => (
          <div key={v.id}
            style={{ background:"#0E1812", border:"1px solid #1C2E22", borderRadius:16, overflow:"hidden" }}>

            {/* Card header */}
            <div style={{ background:"linear-gradient(135deg,#060F09 0%,#0A2015 100%)", padding:"20px 16px 16px" }}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-3xl">{v.icon}</div>
                <div className="flex items-center gap-1">
                  <span style={{ fontSize:9, fontWeight:700, letterSpacing:1, color:"#C9A84C", background:"rgba(201,168,76,0.1)", border:"1px solid rgba(201,168,76,0.2)", padding:"2px 8px", borderRadius:20 }}>
                    {v.id.startsWith('sp') ? v.id.replace('sp','SP-') : '#' + v.id.replace(/^v0*/,'')}
                  </span>
                </div>
              </div>
              <h3 style={{ fontFamily:"Cinzel,serif", color:"#EDE8DC", fontWeight:700, fontSize:13, marginTop:8 }}>
                {v.name}
              </h3>
            </div>

            {/* Heritage */}
            <div style={{ padding:"12px 16px", borderTop:"1px solid #1C2E22" }}>
              <div className="flex items-start gap-2">
                <MapPin className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
                <p style={{ color:"rgba(237,232,220,0.5)", fontSize:11, lineHeight:1.6 }}>
                  {v.heritage}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div style={{ padding:"12px 16px 16px", display:"flex", gap:8 }}>
              {/* View Portal — public */}
              <button
                onClick={() => handleViewPortal(v)}
                style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6, background:"#0F5132", border:"1px solid rgba(201,168,76,0.3)", borderRadius:10, padding:"8px 12px", color:"#C9A84C", fontSize:12, fontWeight:700, cursor:"pointer" }}
              >
                <Eye className="w-3.5 h-3.5" />
                View Portal
              </button>

              {/* Admin Edit — only for logged-in admins */}
              {isAdmin(currentUser) && (
                <button
                  onClick={() => {
                    if (onSelectVillage) {
                      // Pass with admin flag
                      onSelectVillage({ ...v, _adminEdit: true } as any);
                    }
                  }}
                  style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(201,168,76,0.12)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:10, padding:"8px 12px", color:"#C9A84C", fontSize:12, fontWeight:700, cursor:"pointer" }}
                  title="Edit this community portal"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Edit
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign:"center" as const, padding:"48px 0" }}>
          <p style={{ color:"rgba(237,232,220,0.3)", fontSize:13 }}>No communities match your search.</p>
        </div>
      )}
    </div>
  );
};

export default CommunitiesShowcase;
